'use client';

import { useCallback, useState } from 'react';

import { Carousel, CarouselItem } from '@hrworks/sui-extension/Carousel';

import { mq } from '../../util/mediaQueries';
import { Image } from '../Image/Image';
import { Lightbox } from '../Lightbox';
import type { CompanyMediaProps } from './CompanyMedia.types';
import { NavigationButton } from './NavigationButton';

const getYouTubeId = (url?: string | null): string | null => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);

  return match?.[1] || null;
};

export const CompanyMedia = ({ settings, ...otherProps }: CompanyMediaProps) => {
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const imageUrls = (settings.companyPictures?.map((p) => p.url).filter(Boolean) as string[]) || [];
  const youtubeId = getYouTubeId(settings.youtubeUrl);
  const thumbnailUrl = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg` : null;
  const youtubeEmbedUrl = youtubeId ? `https://www.youtube-nocookie.com/embed/${youtubeId}` : null;

  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const handleImageError = useCallback((url: string) => {
    setFailedImages((prev) => {
      const next = new Set(prev);
      next.add(url);

      return next;
    });
  }, []);

  const validImageUrls = imageUrls.filter((url) => !failedImages.has(url));

  if (!thumbnailUrl && validImageUrls.length === 0) {
    return null;
  }

  return (
    <>
      <Carousel
        prevArrow={<NavigationButton data-guide-id="carousel-prev-btn" direction="prev" />}
        nextArrow={<NavigationButton data-guide-id="carousel-next-btn" direction="next" />}
        slideHeight={180}
        showPagination={false}
        data-guide-id="carousel-container"
        {...otherProps}
      >
        {thumbnailUrl && (
          <CarouselItem
            data-guide-id="carousel-item-video"
            onClick={() => setLightboxIndex(0)}
            css={{
              position: 'relative',
              cursor: 'pointer',
              [mq.conditionalTransition]: { transition: 'transform 0.15s', '&:hover': { transform: 'scale(1.02)' } },
              '&::after': {
                content: "'▶'",
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: 48,
                color: 'white',
                textShadow: '0 2px 8px rgba(0,0,0,0.6)',
                pointerEvents: 'none',
              },
            }}
          >
            <Image src={thumbnailUrl} alt="Video" />
          </CarouselItem>
        )}
        {validImageUrls.map((url, index) => (
          <CarouselItem
            key={url}
            data-guide-id="carousel-item"
            onClick={() => setLightboxIndex(index + (youtubeId ? 1 : 0))}
            css={{
              cursor: 'pointer',
              [mq.conditionalTransition]: { transition: 'transform 0.15s', '&:hover': { transform: 'scale(1.02)' } },
            }}
          >
            <Image src={url} alt="Company Picture" onError={() => handleImageError(url)} />
          </CarouselItem>
        ))}
      </Carousel>
      <Lightbox
        images={validImageUrls}
        youtubeEmbedUrl={youtubeEmbedUrl}
        initialIndex={lightboxIndex}
        open={lightboxIndex >= 0}
        onCloseAction={() => setLightboxIndex(-1)}
      />
    </>
  );
};
